import { unique, intersect } from '../utils/array';
import { translate } from '../utils/locale';
import { urlify, splitNewLine } from '../utils/string';

export default (state = {}, action = {}) => {
	if (action.type === 'PRODUCT_CACHE') {
		// Create lookup tables.
		// Sorting done dynamically.

		// Name - Search
		const cache = action.productCache.content;
		const { languages, categories, categoryTitles, categoryTitlesLocalized, productIdLookup } = action.productCache; // Languages WP setting not Centra.
		const reverseLookupMaterial = action.materials.reduce((acc, m) => ({ ...acc, ...splitNewLine(m.matches).reduce((acc2, m2) => ({ ...acc2, [urlify(m2)]: urlify(translate(action.setup, m.title)) }), {}) }), {});
		/*
		{
			'#000000': {
				title: 'Black',
				color: '#000000',
				order: i,
				collection: [],
				variant: [],
			}
		}...
		*/
		const swatchLookup = action.pdpSwatches.reduce((acc, m, i) => {
			if (acc[m.color]) return { ...acc, [m.color]: { ...acc[m.color], variant: [...acc[m.color].variant, urlify(m.title)], collection: [...acc[m.color].collection, ...(m.collection || []).map((c) => c.text)] } };
			return { ...acc, [m.color]: { title: m.name, color: m.color, order: i, variant: [urlify(m.title)], collection: (m.collection || []).map((c) => c.text) } };
		}, {});

		const tempCollectionSwatches = {};
		const productCollectionSwatches = {};
		action.pdpSwatches.forEach((swatch, i) => {
			if (swatch.collection) {
				swatch.collection.forEach((collection) => {
					if (!tempCollectionSwatches[collection.text]) tempCollectionSwatches[collection.text] = {};
					tempCollectionSwatches[collection.text][urlify(swatch.title)] = { ...swatch, order: i };
				});
			}
		});

		const seatHeightKeys = unique(action.setup.statics.seatHeights.map((sH) => parseInt(sH.sizeInInches, 10)));
		const variantKeys = {};

		const filterArrays = {
			categories, // Object mapping category paths to arrays of product keys.
			categoryVariants: {}, // Object mapping category paths to arrays of product keys.
			colors: {}, // Object mapping color names to arrays of product keys.
			collections: {}, // Object mapping color names to arrays of product keys.
			designers: {}, // Object mapping designers to arrays of product keys.
			materials: {}, // Object mapping sizes to arrays of product keys.
			leadTimes: {}, // Object mapping sizes to arrays of product keys.
			seatHeights: {}, // Object mapping sizes to arrays of product keys.
			shapes: {}, // Object mapping sizes to arrays of product keys.
			outdoor: [], // Array of product keys
			stacks: [], // Array of product keys
			productsOnSale: [], // Array of product keys
			newProducts: [], // Array of product keys
		};
		const filterDescriptions = {
			categories: categoryTitles,
			colors: {},
			collections: {},
			designers: {},
			materials: {},
			leadTimes: {},
			seatHeights: {},
			shapes: {},
		};
		const languageKeys = Object.keys(languages);
		const searchTerms = languageKeys.reduce((acc, language) => ({ ...acc, [language]: {} }), {});

		const localized = languageKeys.slice(1).reduce((acc, language) => ({
			...acc,
			[language]: {
				categories: categoryTitlesLocalized ? categoryTitlesLocalized[language] : {},
				colors: {},
				materials: {},
			},
		}), {}); // Merged localized version of above.

		/*
			Localized versions shouldn't create new arrays.
			Their arrays exist but at their non-translated key.
			How to map their keys with their localized versions.
		*/

		Object.keys(cache).forEach((cacheKey) => {
			const item = cache[cacheKey];
			if (item.productHidden) return;
			if (item.sizes.filter((s) => s.availability === '["US"]').length === item.sizes.length && action.setup.country === 'US') return;
			if (item.sizes.filter((s) => s.availability === '["EU"]').length === item.sizes.length && action.setup.country !== 'US') return;
			const { tags = [] } = item;
			const skus = item.sizes ? item.sizes.map((s) => (s?.sku || '').replace(/[^a-z0-9]/gi, '')).join(' ') : '';

			if (!variantKeys[item.id]) variantKeys[item.id] = [];
			variantKeys[item.id].push(cacheKey);

			if (item.variant) {
				const urlifiedItemVariants = Object.keys(item.variations).map((v) => urlify(item.variations[v]));
				const urlifiedItemCollection = urlify(item.collection);

				productCollectionSwatches[cacheKey] = {};
				urlifiedItemVariants.forEach((v) => {
					if (tempCollectionSwatches?.[urlifiedItemCollection]?.[v]) productCollectionSwatches[cacheKey][v] = tempCollectionSwatches[urlifiedItemCollection][v];
				});

				// Colors lookup
				const colorMatched = Object.keys(swatchLookup).filter((colorMatch) => (
					intersect(swatchLookup[colorMatch].variant, urlifiedItemVariants).length > 0
					&& swatchLookup[colorMatch].collection.indexOf(urlifiedItemCollection) !== -1
				));
				colorMatched.forEach((colorMatch) => {
					const { color, order, title } = swatchLookup[colorMatch];
					const colorKey = urlify(translate(action.setup, title));
					if (!filterArrays.colors[colorKey]) {
						filterArrays.colors[colorKey] = [];
						filterDescriptions.colors[colorKey] = { color, order, title };
					}
					filterArrays.colors[colorKey][filterArrays.colors[colorKey].length] = cacheKey;
				});

				// Materials lookup
				Object.keys(reverseLookupMaterial).filter((materialMatch) => (
					urlifiedItemVariants.indexOf(materialMatch) !== -1
					|| tags.indexOf(materialMatch) !== -1
				)).forEach((materialMatch) => {
					if (!filterArrays.materials[reverseLookupMaterial[materialMatch]]) {
						filterArrays.materials[reverseLookupMaterial[materialMatch]] = [];
					}
					filterArrays.materials[reverseLookupMaterial[materialMatch]][filterArrays.materials[reverseLookupMaterial[materialMatch]].length] = cacheKey;
				});
			}

			// Search Terms
			if (item.name.toLowerCase().indexOf('sit with me') === -1) {
				searchTerms[languageKeys[0]][`${item.name} ${item.variant} ${skus} ${tags.join(' ')}`] = searchTerms[languageKeys[0]][`${item.name} ${item.variant} ${skus} ${tags.join(' ')}`] ? [...searchTerms[languageKeys[0]][`${item.name} ${item.variant} ${skus} ${tags.join(' ')}`], cacheKey] : [cacheKey];
			}

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					const isoLanguageCode = language;
					const localizedItem = item.localized ? item.localized[isoLanguageCode] : false;

					// Can't create localized versions if default doesn't exist.
					if (localizedItem && localizedItem.swatch_filter) {
						Object.keys(localizedItem.swatch_filter).forEach((colorKey) => {
							// Color key should exist on default as impossible to create localized version without default.
							localized[isoLanguageCode].colors[urlify(item.swatch_filter[colorKey])] = {
								key: urlify(localizedItem.swatch_filter[colorKey]),
								description: localizedItem.swatch_filter[colorKey],
							};
						});
					}
					// Can't create localized versions if default doesn't exist.
					if (localizedItem && localizedItem.material) {
						Object.keys(localizedItem.material).forEach((materialKey) => {
							// Material key should exist on default as impossible to create localized version without default.
							localized[isoLanguageCode].materials[urlify(item.material[materialKey])] = {
								key: urlify(localizedItem.material[materialKey]),
								description: localizedItem.material[materialKey],
							};
						});
					}

					if (localizedItem && localizedItem.name) {
						searchTerms[isoLanguageCode][`${localizedItem.name} ${localizedItem.variant}`] = searchTerms[isoLanguageCode][`${localizedItem.name} ${localizedItem.variant}`] ? [...searchTerms[isoLanguageCode][`${localizedItem.name} ${localizedItem.variant}`], cacheKey] : [cacheKey];
					} else {
						searchTerms[isoLanguageCode][`${item.name} ${item.variant}`] = searchTerms[isoLanguageCode][`${item.name} ${item.variant}`] ? [...searchTerms[isoLanguageCode][`${item.name} ${item.variant}`], cacheKey] : [cacheKey];
					}
				});
			}

			// Brands lookup
			if (item.brand) {
				const designerKey = urlify(item.brand);
				if (!filterArrays.designers[designerKey]) {
					filterArrays.designers[designerKey] = [];
					filterDescriptions.designers[designerKey] = item.brand;
				}
				filterArrays.designers[designerKey][filterArrays.designers[designerKey].length] = cacheKey;
			}

			// Collections lookup
			if (item.collection) {
				const collectionKey = urlify(item.collection);
				if (!filterArrays.collections[collectionKey]) {
					filterArrays.collections[collectionKey] = [];
					filterDescriptions.collections[collectionKey] = item.collection;
				}
				filterArrays.collections[collectionKey][filterArrays.collections[collectionKey].length] = cacheKey;
			}

			// Seat Heights lookup
			const seatHeights = item.sizes ? item.sizes.map((size) => size.seatHeight && size.seatHeight.match(/^[0-9.]+/)[0]).filter((size) => !!size) : [];

			seatHeights.forEach((seatHeightKey) => {
				// Find closest size to keys.
				const closestKey = { key: false, difference: 10000 };
				seatHeightKeys.forEach((k) => {
					const difference = Math.abs(k - Math.ceil(parseFloat(seatHeightKey)));
					if (difference < closestKey.difference) {
						closestKey.key = k;
						closestKey.difference = difference;
					}
				});

				if (!filterArrays.seatHeights[closestKey.key]) {
					filterArrays.seatHeights[closestKey.key] = [];
					filterDescriptions.seatHeights[closestKey.key] = action.setup.statics.seatHeights.filter((sH) => closestKey.key === parseInt(sH.sizeInInches, 10)).map((sh) => sh.label)[0].text;
				}
				filterArrays.seatHeights[closestKey.key][filterArrays.seatHeights[closestKey.key].length] = cacheKey;
			});

			// Lead Time lookup
			let market = action?.setup?.market;
			if (typeof market === 'number') market = String(typeof action.setup.market);
			if (!Array.isArray(market)) market = [market];
			const leadTime = market && market.indexOf('65580597415') === -1 ? item.leadTime : item.leadTimeEu;
			if (leadTime) {
				const leadTimeKey = urlify(leadTime);
				if (!filterArrays.leadTimes[leadTimeKey]) {
					filterArrays.leadTimes[leadTimeKey] = [];
					filterDescriptions.leadTimes[leadTimeKey] = leadTime;
				}
				filterArrays.leadTimes[leadTimeKey][filterArrays.leadTimes[leadTimeKey].length] = cacheKey;
			}

			// Shape lookup
			if (item.shape) {
				const shapeKey = urlify(item.shape);
				if (!filterArrays.shapes[shapeKey]) {
					filterArrays.shapes[shapeKey] = [];
					filterDescriptions.shapes[shapeKey] = item.shape;
				}
				filterArrays.shapes[shapeKey][filterArrays.shapes[shapeKey].length] = cacheKey;
			}

			if (item.stacks) filterArrays.stacks[filterArrays.stacks.length] = cacheKey;
			if (item.outdoorUse) filterArrays.outdoor[filterArrays.outdoor.length] = cacheKey;

			// Sale / News
			// if (item.price.showAsOnSale) filterArrays.productsOnSale[filterArrays.productsOnSale.length] = cacheKey;
			// if (item.price.newProduct) filterArrays.newProducts[filterArrays.newProducts.length] = cacheKey;
		});

		// Category Variants lookup
		if (filterArrays.categories) {
			Object.keys(filterArrays.categories).forEach((category) => {
				filterArrays.categories[category].forEach((item) => {
					if (!variantKeys[item]) return;
					if (!filterArrays.categoryVariants[category]) filterArrays.categoryVariants[category] = [];
					filterArrays.categoryVariants[category] = [...filterArrays.categoryVariants[category], ...variantKeys[item]];
				});
			});
		}

		/*
			Extend search terms.
		*/

		// Categories
		Object.keys(filterDescriptions.categories).forEach((category) => {
			const categorySearchTerm = searchTerms[languageKeys[0]][filterDescriptions.categories[category]];
			searchTerms[languageKeys[0]][filterDescriptions.categories[category]] = !categorySearchTerm ? filterArrays.categories[category] : [...categorySearchTerm, ...filterArrays.categories[category]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					if (localized[language] && localized[language].categories && localized[language].categories[category]) {
						const localizedCategorySearchTerm = searchTerms[language][localized[language].categories[category].description];
						searchTerms[language][localized[language].categories[category].description] = !localizedCategorySearchTerm ? filterArrays.categories[category] : [...localizedCategorySearchTerm, ...filterArrays.categories[category]];
					} else {
						const localizedCategorySearchTerm = searchTerms[language][filterDescriptions.categories[category]];
						searchTerms[language][filterDescriptions.categories[category]] = !localizedCategorySearchTerm ? filterArrays.categories[category] : [...localizedCategorySearchTerm, ...filterArrays.categories[category]];
					}
				});
			}
		});

		// Designers
		Object.keys(filterDescriptions.designers).forEach((designer) => {
			const designerSearchTerm = searchTerms[languageKeys[0]][filterDescriptions.designers[designer]];
			searchTerms[languageKeys[0]][filterDescriptions.designers[designer]] = !designerSearchTerm ? filterArrays.designers[designer] : [...designerSearchTerm, ...filterArrays.designers[designer]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					// Copy default language
					searchTerms[language][filterDescriptions.designers[designer]] = [...searchTerms[languageKeys[0]][filterDescriptions.designers[designer]]];
				});
			}
		});

		// Collections
		Object.keys(filterDescriptions.collections).forEach((collection) => {
			const collectionSearchTerm = searchTerms[languageKeys[0]][filterDescriptions.collections[collection]];
			searchTerms[languageKeys[0]][filterDescriptions.collections[collection]] = !collectionSearchTerm ? filterArrays.collections[collection] : [...collectionSearchTerm, ...filterArrays.collections[collection]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					// Copy default language
					searchTerms[language][filterDescriptions.collections[collection]] = [...searchTerms[languageKeys[0]][filterDescriptions.collections[collection]]];
				});
			}
		});

		// Shape
		Object.keys(filterDescriptions.shapes).forEach((shape) => {
			const shapeSearchTerm = searchTerms[languageKeys[0]][filterDescriptions.shapes[shape]];
			searchTerms[languageKeys[0]][filterDescriptions.shapes[shape]] = !shapeSearchTerm ? filterArrays.shapes[shape] : [...shapeSearchTerm, ...filterArrays.shapes[shape]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					// Copy default language
					searchTerms[language][filterDescriptions.shapes[shape]] = [...searchTerms[languageKeys[0]][filterDescriptions.shapes[shape]]];
				});
			}
		});

		return {
			...state,
			[action.market]: {
				...state[action.market],
				[action.pricelist]: {
					products: cache,
					productIdLookup,
					filterArrays,
					filterDescriptions,
					productCollectionSwatches,
					localized,
					searchTerms,
				},
			},
		};
	}

	return state;
};
